import { Button, Dropdown, Tag, Table, Modal, Select, Input, Space, message } from "antd"
import {
  MoreOutlined,
  CloudUploadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  MailOutlined,
  FileDoneOutlined,
} from "@ant-design/icons"
import { useEffect, useMemo, useState } from "react"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import apiClient from "../../../Utils/apiClient"
import PanelLayout from "../../../Layout/PanelLayout"
import TableBox from "../../../components/TableBox/TableBox"
import HostnameLink from "../../../components/HostnameLink/HostnameLink"
import DeviceModelImage from "../../../components/DeviceModelImage/DeviceModelImage"
import EmptyState from "../../../components/EmptyState/EmptyState"
import StatusTag from "../../../components/StatusTag/StatusTag"
import AutoCompleteBox from "../../../components/AutoCompleteBox/AutoCompleteBox"
import { getStoredPageSize } from "../../../Utils/TableUtils/TableUtils"
import { renderDate } from "../../../Utils/utils"
import { resolveDeviceModelImage } from "../../../Utils/deviceModelImage"
import "../../Operations/Operations.scss"
import "./DevicePatches.scss"

dayjs.extend(relativeTime)

const shortUser = (value) => {
  const text = String(value || "").trim()
  if (!text) return "Unassigned"
  const parts = text.split(/\\|\//)
  return parts[parts.length - 1] || text
}

const VIEW_LABELS = {
  all: "All",
  pending: "With pending",
  current: "Up to date",
  reboot: "Reboot pending",
}

const DevicePatches = () => {
  const page = "Device Patches"
  const [filters, setFilters] = useState({ hostname: "", user: "" })
  const [viewChip, setViewChip] = useState("pending")
  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: getStoredPageSize(page) || 10,
    total: 0,
  })
  const [loading, setLoading] = useState(true)
  const [devices, setDevices] = useState([])
  const [summary, setSummary] = useState({
    all: 0,
    pending: 0,
    current: 0,
    rebootPending: 0,
    patchItems: 0,
  })
  const [busyHost, setBusyHost] = useState("")
  const [reportOpen, setReportOpen] = useState(false)
  const [reportView, setReportView] = useState("pending")
  const [reportTo, setReportTo] = useState("")
  const [exporting, setExporting] = useState("")
  const [emailing, setEmailing] = useState(false)

  const reportParams = () => ({
    view: reportView || "all",
    hostname: filters.hostname || undefined,
    user: filters.user || undefined,
  })

  const openReportModal = () => {
    setReportView(viewChip || "pending")
    setReportOpen(true)
  }

  const downloadEnterpriseReport = async (format = "xlsx") => {
    setExporting(format === "pdf" ? "pdf" : "xlsx")
    try {
      const res = await apiClient("reports/device-patches/export", "GET", null, {
        params: { ...reportParams(), format },
        responseType: "blob",
      })
      const url = URL.createObjectURL(res.blob)
      const a = document.createElement("a")
      a.href = url
      a.download =
        res.filename ||
        `DM-Windows-Device-Patch-Report-${reportView}-${dayjs().format("YYYYMMDD")}.${format === "pdf" ? "pdf" : "xlsx"}`
      a.click()
      URL.revokeObjectURL(url)
      message.success(
        format === "pdf" ? "PDF report downloaded" : "Excel report downloaded"
      )
      setReportOpen(false)
    } catch (err) {
      message.error(err?.message || "Could not generate report")
    } finally {
      setExporting("")
    }
  }

  const emailEnterpriseReport = async () => {
    setEmailing(true)
    try {
      const res = await apiClient("reports/device-patches/email", "POST", {
        ...reportParams(),
        to: reportTo.trim() || undefined,
        formats: ["xlsx", "pdf"],
      })
      if (res?.success) {
        message.success(res.message || "Report emailed")
        setReportOpen(false)
      } else {
        message.error(res?.message || "Could not email report")
      }
    } catch (err) {
      message.error(err?.message || "Could not email report")
    } finally {
      setEmailing(false)
    }
  }

  const fetchData = async (
    customPage = paginationData.page,
    customPageSize = paginationData.pageSize,
    customFilters = filters,
    customView = viewChip
  ) => {
    setLoading(true)
    try {
      const params = {
        page: customPage,
        pageSize: customPageSize,
        hostname: customFilters.hostname || undefined,
        user: customFilters.user || undefined,
        view: customView || "all",
      }
      const res = await apiClient("devices/device-patches", "GET", null, { params })
      if (res?.data) {
        setDevices(res.data)
        setPaginationData(
          res.pageData || { page: customPage, pageSize: customPageSize, total: 0 }
        )
        if (res.summary) setSummary(res.summary)
      }
    } catch (err) {
      console.log("Error fetching device patches", err)
      message.error("Failed to load device patches")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData(1, paginationData.pageSize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleTableChange = (pagination) => {
    const { current, pageSize } = pagination
    setPaginationData((prev) => ({ ...prev, page: current, pageSize }))
    fetchData(current, pageSize, filters, viewChip)
  }

  const handleClearFilter = () => {
    const empty = { hostname: "", user: "" }
    setFilters(empty)
    setViewChip("pending")
    setPaginationData((prev) => ({ ...prev, page: 1 }))
    fetchData(1, paginationData.pageSize, empty, "pending")
  }

  const handleScan = async (device) => {
    setBusyHost(device.hostname)
    try {
      const res = await apiClient(
        `devices/${encodeURIComponent(device.hostname)}/windows-update-scan`,
        "POST"
      )
      message.success(res?.message || `Scan queued for ${device.hostname}`)
    } catch (err) {
      message.error(err?.message || "Could not queue scan")
    } finally {
      setBusyHost("")
    }
  }

  const handleUpdate = async (device) => {
    setBusyHost(device.hostname)
    try {
      const res = await apiClient(
        `devices/apply-windows-updates/${encodeURIComponent(device.hostname)}`,
        "POST"
      )
      if (res?.success || res?.status === 200) {
        message.success(res.message || `Update queued for ${device.hostname}`)
      } else {
        message.error(res?.message || "Could not queue update")
      }
    } catch (err) {
      message.error(err?.message || "Could not queue update")
    } finally {
      setBusyHost("")
    }
  }

  const viewKpis = useMemo(
    () => [
      {
        key: "all",
        label: "All devices",
        value: summary.all,
        tone: "#2563eb",
        soft: "rgba(37, 99, 235, 0.12)",
      },
      {
        key: "pending",
        label: "With pending",
        value: summary.pending,
        tone: "#d97706",
        soft: "rgba(245, 158, 11, 0.16)",
      },
      {
        key: "current",
        label: "Up to date",
        value: summary.current,
        tone: "#059669",
        soft: "rgba(34, 197, 94, 0.14)",
      },
      {
        key: "reboot",
        label: "Reboot pending",
        value: summary.rebootPending,
        tone: "#7c3aed",
        soft: "rgba(124, 58, 237, 0.12)",
      },
    ],
    [summary]
  )

  const patchColumns = [
    {
      title: "KB",
      dataIndex: "kb",
      key: "kb",
      width: 120,
      render: (kb) =>
        kb ? <span className="ops-name">KB{kb}</span> : <span className="ops-muted">—</span>,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
      render: (title) => title || <span className="ops-muted">—</span>,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 170,
      render: (category) => (
        <Tag className="device-patches__cat">{category || "Other"}</Tag>
      ),
    },
    {
      title: "Release",
      dataIndex: "releaseDate",
      key: "releaseDate",
      width: 140,
      render: (value) =>
        value ? renderDate(value) : <span className="ops-muted">—</span>,
    },
  ]

  const columns = [
    {
      title: "Device",
      dataIndex: "hostname",
      key: "hostname",
      width: 300,
      render: (text, record) => {
        const model = resolveDeviceModelImage(record)
        return (
          <div className="inv-device">
            <DeviceModelImage device={record} size="sm" />
            <div className="inv-device__copy">
              <div className="inv-device__title">
                <span
                  className={`inv-device__pulse inv-device__pulse--${String(
                    record.deviceStatus || "offline"
                  )
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                  aria-hidden
                />
                <HostnameLink record={record}>{text}</HostnameLink>
              </div>
              <div className="inv-device__meta" title={record.user || ""}>
                <span>{shortUser(record.user)}</span>
                <span className="inv-device__dot" />
                <span title={model.displayModel}>{model.label}</span>
              </div>
            </div>
          </div>
        )
      },
    },
    {
      title: "Online",
      dataIndex: "deviceStatus",
      key: "deviceStatus",
      width: 110,
      render: (value) => (value ? <StatusTag status={value} /> : "—"),
    },
    {
      title: "Pending",
      dataIndex: "pendingCount",
      key: "pendingCount",
      width: 120,
      render: (count) =>
        count > 0 ? (
          <StatusTag status={`${count} pending`} color="orange" />
        ) : (
          <StatusTag status="Current" color="success" />
        ),
    },
    {
      title: "Categories",
      key: "categories",
      width: 220,
      render: (_, record) => {
        const cats = record.categories || []
        if (!cats.length) return <span className="ops-muted">—</span>
        const shown = cats.slice(0, 2)
        const extra = cats.length - shown.length
        return (
          <div className="device-patches__cats">
            {shown.map((cat) => (
              <Tag key={cat} className="device-patches__cat" title={cat}>
                {cat}
              </Tag>
            ))}
            {extra > 0 ? (
              <Tag className="device-patches__cat" title={cats.join(", ")}>
                +{extra}
              </Tag>
            ) : null}
          </div>
        )
      },
    },
    {
      title: "Last sync",
      dataIndex: "lastSyncTime",
      key: "lastSyncTime",
      width: 160,
      render: (value) =>
        value ? (
          <div className="device-patches__when">
            <span>{renderDate(value)}</span>
            <span className="ops-muted">{dayjs(value).fromNow()}</span>
          </div>
        ) : (
          <span className="ops-muted">—</span>
        ),
    },
    {
      title: "",
      key: "actions",
      width: 56,
      align: "right",
      fixed: "right",
      render: (_, record) => (
        <Dropdown
          trigger={["click"]}
          placement="bottomRight"
          menu={{
            items: [
              {
                key: "scan",
                label: "Scan now",
                disabled: busyHost === record.hostname,
                onClick: () => handleScan(record),
              },
              record.pendingCount > 0
                ? {
                    key: "update",
                    label: "Install pending",
                    icon: <CloudUploadOutlined />,
                    disabled: busyHost === record.hostname,
                    onClick: () => handleUpdate(record),
                  }
                : null,
            ].filter(Boolean),
          }}
        >
          <Button type="text" icon={<MoreOutlined />} aria-label="Patch actions" />
        </Dropdown>
      ),
    },
  ]

  return (
    <PanelLayout
      title="Device patches"
      subtitle="Windows patches reported by each device from scheduled or live scans. Expand a row for KB-level detail."
      actions={
        <Button type="primary" icon={<FileDoneOutlined />} onClick={openReportModal}>
          Generate report
        </Button>
      }
    >
      <div className="device-patches">
        <div className="ops-kpis ops-kpis--4">
          {viewKpis.map((item) => (
            <button
              type="button"
              key={item.key}
              className={`ops-kpi${viewChip === item.key ? " is-active" : ""}`}
              style={{ "--act-tone": item.tone, "--act-soft": item.soft }}
              onClick={() => {
                setViewChip(item.key)
                setPaginationData((prev) => ({ ...prev, page: 1 }))
                fetchData(1, paginationData.pageSize, filters, item.key)
              }}
            >
              <div>
                <span className="ops-kpi-label">{item.label}</span>
                <strong>{item.value}</strong>
              </div>
            </button>
          ))}
        </div>

        <div className="ops-toolbar">
          <span className="ops-toolbar__count">
            {paginationData.total} device{paginationData.total === 1 ? "" : "s"}
            {viewChip ? ` · ${VIEW_LABELS[viewChip] || viewChip}` : ""}
            {summary.patchItems != null ? ` · ${summary.patchItems} patch items` : ""}
          </span>
          <div className="ops-toolbar__actions">
            <Button size="small" icon={<FileExcelOutlined />} onClick={openReportModal}>
              Report
            </Button>
            <Button
              size="small"
              onClick={() =>
                fetchData(paginationData.page, paginationData.pageSize, filters, viewChip)
              }
            >
              Refresh
            </Button>
          </div>
        </div>

        <Modal
          title="Enterprise patch report"
          open={reportOpen}
          onCancel={() => setReportOpen(false)}
          footer={null}
          destroyOnClose
          width={520}
        >
          <p className="ops-muted" style={{ marginBottom: 16 }}>
            Excel workbook (full detail) and PDF summary. Email attaches both formats for
            IT.
          </p>
          <div className="mb-3">
            <label className="ops-filter-label">Report scope</label>
            <Select
              className="w-100"
              value={reportView}
              onChange={setReportView}
              options={[
                { value: "all", label: "All devices" },
                { value: "pending", label: "With pending patches" },
                { value: "current", label: "Up to date only" },
                { value: "reboot", label: "Reboot pending" },
              ]}
            />
          </div>
          <div className="mb-3">
            <label className="ops-filter-label">Email recipients (optional)</label>
            <Input
              allowClear
              placeholder="Defaults to DEVICE_REPORT_MAIL_ID / admin"
              value={reportTo}
              onChange={(e) => setReportTo(e.target.value)}
            />
          </div>
          {(filters.hostname || filters.user) && (
            <p className="ops-muted" style={{ marginBottom: 16 }}>
              Active table filters will apply
              {filters.hostname ? ` · hostname: ${filters.hostname}` : ""}
              {filters.user ? ` · user: ${filters.user}` : ""}
            </p>
          )}
          <Space wrap>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              loading={exporting === "xlsx"}
              onClick={() => downloadEnterpriseReport("xlsx")}
            >
              Download Excel
            </Button>
            <Button
              icon={<FilePdfOutlined />}
              loading={exporting === "pdf"}
              onClick={() => downloadEnterpriseReport("pdf")}
            >
              Download PDF
            </Button>
            <Button
              icon={<MailOutlined />}
              loading={emailing}
              onClick={emailEnterpriseReport}
            >
              Email Excel + PDF
            </Button>
          </Space>
        </Modal>

        {devices.length || loading ? (
          <TableBox
            page={page}
            title={null}
            columns={columns}
            data={devices}
            loading={loading}
            className="dashboard-inventory-table device-patches__table"
            rowKey="_id"
            pagination={{
              current: paginationData.page,
              pageSize: paginationData.pageSize,
              total: paginationData.total,
              showSizeChanger: true,
            }}
            onChange={handleTableChange}
            expandable={{
              rowExpandable: (record) => (record.patches || []).length > 0,
              expandedRowRender: (record) => (
                <Table
                  className="device-patches__nested"
                  size="small"
                  pagination={false}
                  rowKey={(row) =>
                    `${row.updateId || ""}-${row.kb || ""}-${row.title || ""}`
                  }
                  columns={patchColumns}
                  dataSource={record.patches || []}
                />
              ),
            }}
            enableFilter
            filtercontent={
              <div className="p-3">
                <AutoCompleteBox
                  label="Hostname"
                  name="hostname"
                  className="mb-3 w-100"
                  endPoint="devices/identifiers"
                  placeholder="Search hostname"
                  value={filters.hostname}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, hostname: value }))
                  }
                  onSelect={(value) => {
                    const next = { ...filters, hostname: value }
                    setFilters(next)
                    setPaginationData((prev) => ({ ...prev, page: 1 }))
                    fetchData(1, paginationData.pageSize, next, viewChip)
                  }}
                />
                <AutoCompleteBox
                  label="User"
                  name="user"
                  className="mb-3 w-100"
                  endPoint="users"
                  placeholder="Search user"
                  value={filters.user}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, user: value }))
                  }
                  onSelect={(value) => {
                    const next = { ...filters, user: value }
                    setFilters(next)
                    setPaginationData((prev) => ({ ...prev, page: 1 }))
                    fetchData(1, paginationData.pageSize, next, viewChip)
                  }}
                />
              </div>
            }
            onFilter={() => {
              setPaginationData((prev) => ({ ...prev, page: 1 }))
              fetchData(1, paginationData.pageSize, filters, viewChip)
            }}
            onClear={handleClearFilter}
            filters={{ ...filters, view: viewChip }}
          />
        ) : (
          <EmptyState
            title="No devices"
            description="No Windows clients match this patch view. Wait for the scheduled scan, or run Scan now on a device."
          />
        )}
      </div>
    </PanelLayout>
  )
}

export default DevicePatches
